import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';

import Map from '@App/components/Map/Map';
import Question5Form from '@App/pages/Tablet/Question5/Question5Form';
import natureAreas from '@App/components/Map/data/nature-areas';

const QUESTION_ID = 'question5';

function buildQuestionData() {
  return natureAreas;
}

function getQuestionLayers({ data, lastAnswer, options: { animationTime } }) {
  const answer = lastAnswer?.questionId === QUESTION_ID ? lastAnswer.answer : null;
  const pulsingData = data.map((area, index) => ({
    ...area,
    labelOpacity: Math.round(255 * (
      (Math.sin(animationTime * 0.35 + index * 0.7) + 1) / 2
    )),
    opacity: 125 + Math.round(100 * (
      (Math.sin(animationTime * 0.35 + index * 0.7) + 1) / 2
    )),
  }));
  const labelData = pulsingData.filter(area => (
    !answer?.coordinates
      || area.coordinates[0] !== answer.coordinates[0]
      || area.coordinates[1] !== answer.coordinates[1]
  ));
  const selectedAnswer = answer?.coordinates
    ? [{
      coordinates: answer.coordinates,
      name: answer.label || answer.value,
    }]
    : [];

  return [new ScatterplotLayer({
    id: 'question-5-nature-glow',
    data: pulsingData,
    getFillColor: item => [114, 205, 161, item.opacity],
    getPosition: item => item.coordinates,
    getRadius: item => 450 + item.opacity * 2,
    radiusMinPixels: 4,
    radiusMaxPixels: 36,
  }), new TextLayer({
    id: 'question-5-nature-stars',
    data: pulsingData,
    getColor: item => [255, 226, 146, item.labelOpacity],
    getPosition: item => item.coordinates,
    getSize: item => 16 + Math.round(item.labelOpacity / 64),
    getText: () => '✦',
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'center',
    outlineColor: [255, 255, 255, 90],
    outlineWidth: 2,
    pickable: false,
  }), new TextLayer({
    id: 'question-5-nature-labels',
    data: labelData,
    getColor: item => [196, 239, 205, item.labelOpacity],
    getPosition: item => item.coordinates,
    getSize: 14,
    getText: item => item.name,
    getPixelOffset: [0, -18],
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'bottom',
    outlineColor: item => [20, 31, 46, item.labelOpacity],
    outlineWidth: 3,
  }), new TextLayer({
    id: 'question-5-selected-label',
    data: selectedAnswer,
    getColor: [255, 255, 255, 255],
    getPosition: item => item.coordinates,
    getSize: 16,
    getText: item => item.name,
    getPixelOffset: [0, -18],
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'bottom',
    outlineColor: [20, 31, 46, 255],
    outlineWidth: 3,
    pickable: false,
  })];
}

function Question5(props) {
  const { withInputFields = false, ...visualizationProps } = props;
  const [animationTime, setAnimationTime] = useState(0);
  const { lastAnswer: contextLastAnswer } = useOutletContext() || {};
  const lastAnswer = props.lastAnswer ?? contextLastAnswer;
  const focusCoordinates = lastAnswer?.questionId === QUESTION_ID
    ? lastAnswer.answer?.coordinates || null
    : null;

  useEffect(() => {
    let animationFrame;
    const animate = (timestamp) => {
      setAnimationTime(timestamp / 1000);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <Map
      questionId={QUESTION_ID}
      buildData={buildQuestionData}
      getLayers={getQuestionLayers}
      focusCoordinates={focusCoordinates}
      returnToOverview
      layerOptions={{ animationTime }}
      mapOpacity={0.94}
      {...visualizationProps}
    >
      {withInputFields && <Question5Form />}
    </Map>
  );
}

export default Question5;
